/**
 * The in-VR menu panel.
 *
 * Drawn with the 2D canvas API onto a texture rather than composed from meshes or an HTML
 * overlay. An immersive session renders only what is in the 3D scene — the page's DOM is
 * not visible — and canvas drawing gives crisp text and full layout control without
 * pulling in a UI toolkit.
 *
 * Geometry comes from `menuLayout`, which the raycasting below shares, so what is painted
 * and what is clickable cannot disagree.
 */

import {
  CanvasTexture,
  CircleGeometry,
  Group,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  type Object3D,
  PlaneGeometry,
  Quaternion,
  Raycaster,
  SRGBColorSpace,
  Vector3,
} from 'three';

import { roomScaleOf } from '../domain/nudge.js';
import { boundsSize } from '../domain/scaling.js';
import type { ModelId, ModelMeta } from '../domain/types.js';
import { describeContents, describeRoomScale, formatDimensions } from '../format.js';
import {
  accountStatusLabel,
  layoutMenu,
  PANEL_HEIGHT_M,
  PANEL_HEIGHT_PX,
  PANEL_WIDTH_M,
  PANEL_WIDTH_PX,
  regionAt,
  roomStatusLabel,
  clampScroll,
  type MenuAction,
  type MenuLayout,
  type MenuModel,
  type MenuRegion,
} from './menuLayout.js';

/** How far in front of the viewer the panel is summoned, in metres. */
const SUMMON_DISTANCE_M = 0.85;

/** How far below eye level the panel centres, so it does not cover the model. */
const SUMMON_DROP_M = 0.12;

/** Where the "Models" heading ends, so the account status cannot run back into it. */
const ACCOUNT_STATUS_LEFT_LIMIT = 240;

/** The disc at the head of each row marking whether that model is out in the room. */
const MARKER_RADIUS = 9;

/** Clear space between a row's text and the size percentage at the end of it. */
const SCALE_GAP = 22;

/** Matches the desktop stylesheet, so the two UIs read as the same application. */
const COLOURS = {
  panel: '#1b2128',
  panelEdge: '#39424d',
  row: '#0f1317',
  rowSelected: '#1d3550',
  rowHover: 'rgba(77, 163, 255, 0.16)',
  buttonHover: '#2b3a4a',
  buttonDisabled: '#141a1f',
  text: '#e6ecf2',
  textMuted: '#94a3b1',
  textDisabled: '#4a5560',
  accent: '#4da3ff',
  danger: '#ff6b6b',
  // Deliberately the same amber as the box drawn round a resized model in the room, so
  // that the warning in the list and the warning in front of you are one thing.
  resized: '#ffb454',
} as const;

/** Where a controller's ray meets the panel. */
export interface MenuHit {
  /** The control under the ray, or undefined if it landed on bare panel. */
  readonly region: MenuRegion | undefined;
  /** Distance from the controller, so the ray can be drawn stopping at the panel. */
  readonly distance: number;
  /** World-space point of contact. */
  readonly point: Vector3;
}

export interface VrMenuCallbacks {
  /** Put this model in the room, or take it out if it is already there. */
  readonly onToggle: (id: ModelId) => void;
  /** Delete it from the library outright. Nothing to do with the room. */
  readonly onDelete: (id: ModelId) => void;
  readonly onExit: () => void;
}

export class VrMenu {
  readonly group = new Group();

  private readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D;
  private readonly texture: CanvasTexture;
  private readonly panel: Mesh<PlaneGeometry, MeshBasicMaterial>;
  private readonly cursor: Mesh<CircleGeometry, MeshBasicMaterial>;
  private readonly raycaster = new Raycaster();

  // Scratch values, so aiming and summoning allocate nothing per frame. Never pass a
  // live object3D transform as an out-parameter to getWorld*; that writes through.
  private readonly scratchQuaternion = new Quaternion();
  private readonly scratchVector = new Vector3();

  private models: MenuModel[] = [];
  private accountEmail: string | undefined;
  private scrollOffset = 0;
  private layout: MenuLayout;
  private hoverKey: string | undefined;

  constructor(private readonly callbacks: VrMenuCallbacks) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = PANEL_WIDTH_PX;
    this.canvas.height = PANEL_HEIGHT_PX;

    const context = this.canvas.getContext('2d');
    if (!context) throw new Error('Could not get a 2D context to draw the VR menu.');
    this.context = context;

    this.texture = new CanvasTexture(this.canvas);
    this.texture.colorSpace = SRGBColorSpace;
    this.texture.minFilter = LinearFilter;
    this.texture.generateMipmaps = false;

    // Unlit, and drawn over everything else. A menu that the scene's lighting could dim,
    // or that the model could poke through, would be a menu you cannot reliably read.
    this.panel = new Mesh(
      new PlaneGeometry(PANEL_WIDTH_M, PANEL_HEIGHT_M),
      new MeshBasicMaterial({ map: this.texture, transparent: true, depthTest: false }),
    );
    this.panel.renderOrder = 10;

    this.cursor = new Mesh(
      new CircleGeometry(0.006, 24),
      new MeshBasicMaterial({ color: 0xffffff, depthTest: false, transparent: true }),
    );
    this.cursor.renderOrder = 11;
    this.cursor.visible = false;

    this.group.add(this.panel, this.cursor);
    this.group.visible = false;

    this.layout = layoutMenu(this.models, this.scrollOffset);
    this.paint();
  }

  get isOpen(): boolean {
    return this.group.visible;
  }

  /**
   * Summon the panel in front of the viewer.
   *
   * The direction is taken from where they are looking but flattened to horizontal, so
   * glancing at the floor or ceiling as you press the button does not fling the menu
   * somewhere unreadable.
   */
  open(head: Object3D): void {
    const headPosition = new Vector3().setFromMatrixPosition(head.matrixWorld);

    const forward = new Vector3(0, 0, -1)
      .applyQuaternion(head.getWorldQuaternion(this.scratchQuaternion))
      .setY(0);

    // Looking straight up or down leaves nothing to flatten; fall back to due north.
    if (forward.lengthSq() < 1e-6) forward.set(0, 0, -1);
    forward.normalize();

    this.group.position
      .copy(headPosition)
      .addScaledVector(forward, SUMMON_DISTANCE_M)
      .setY(headPosition.y - SUMMON_DROP_M);

    this.group.lookAt(headPosition);
    this.group.visible = true;

    // Bring the world matrix up to date immediately. Only `render` refreshes the scene
    // graph, and that happens after the per-frame update, so a ray cast in this same
    // frame would otherwise be tested against wherever the panel used to be.
    this.group.updateMatrixWorld(true);

    this.hoverKey = undefined;
    this.cursor.visible = false;
    this.paint();
  }

  close(): void {
    this.group.visible = false;
    this.cursor.visible = false;
    this.hoverKey = undefined;
  }

  toggle(head: Object3D): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open(head);
    }
  }

  /** Replace the listed models. Safe to call while the menu is closed. */
  setLibrary(models: readonly ModelMeta[], inRoom: readonly ModelId[]): void {
    const placed = new Set(inRoom);

    this.models = models.map((meta) => {
      // The dimensions beside it stay the file's own. They are what the model measures,
      // and a resize is a decision about how to look at it rather than a claim about how
      // big it is — which is exactly why the percentage is shown separately.
      const scaleLabel = describeRoomScale(roomScaleOf(meta));

      return {
        id: meta.id,
        name: meta.name,
        detail: `${formatDimensions(boundsSize(meta.bounds))} · ${describeContents(meta)}`,
        inRoom: placed.has(meta.id),
        ...(scaleLabel === undefined ? {} : { scaleLabel }),
      };
    });

    this.scrollOffset = clampScroll(this.scrollOffset, this.models.length);
    this.relayout();
  }

  /**
   * Show who is signed in, or that nobody is.
   *
   * Read-only by design — see [`accountStatusLabel`]. Safe to call while the menu is
   * closed, and cheap enough to call on every change: repainting is one canvas draw.
   */
  setAccount(email: string | undefined): void {
    if (this.accountEmail === email) return;
    this.accountEmail = email;
    this.paint();
  }

  /**
   * Ask where a controller is pointing.
   *
   * A pure query: it reports the hit and changes nothing. Updating the highlight from
   * here would mean the second controller sampled each frame overwrote whatever the
   * first found, so a hand pointing at the menu would be cancelled out by the hand that
   * was not. Deciding between the two is `setPointer`'s job.
   */
  probe(controller: Object3D): MenuHit | undefined {
    if (!this.isOpen) return undefined;

    // Both transforms have to be current before casting. The controller is posed by the
    // runtime and the panel by `open`, but neither propagates to `matrixWorld` until the
    // renderer walks the graph — which is after this runs, and later still for a click
    // arriving on a controller event.
    controller.updateMatrixWorld(true);
    this.group.updateMatrixWorld(true);

    this.raycaster.ray.origin.setFromMatrixPosition(controller.matrixWorld);
    this.raycaster.ray.direction
      .set(0, 0, -1)
      .applyQuaternion(controller.getWorldQuaternion(this.scratchQuaternion))
      .normalize();

    const [hit] = this.raycaster.intersectObject(this.panel, false);
    if (!hit?.uv) return undefined;

    return {
      region: regionAt(this.layout, hit.uv.x, hit.uv.y),
      distance: hit.distance,
      point: hit.point,
    };
  }

  /**
   * Put the cursor and the highlight where a controller is pointing, or clear both when
   * neither hand is pointing at the panel.
   */
  setPointer(hit: MenuHit | undefined): void {
    if (!hit) {
      this.cursor.visible = false;
      this.setHover(undefined);
      return;
    }

    // Float the cursor just off the panel's face so it is not lost to z-fighting.
    this.cursor.position.copy(
      this.group.worldToLocal(this.scratchVector.copy(hit.point)),
    );
    this.cursor.position.z = 0.001;
    this.cursor.visible = true;

    this.setHover(hit.region?.key);
  }

  /**
   * Act on a region the user has clicked.
   *
   * Scrolling is handled internally since it only moves the menu's own view; everything
   * else is the application's business and goes out through the callbacks.
   */
  activate(region: MenuRegion): void {
    const action: MenuAction = region.action;

    switch (action.type) {
      case 'scroll':
        this.scrollOffset = clampScroll(
          this.scrollOffset + action.delta,
          this.models.length,
        );
        this.relayout();
        return;
      case 'toggle':
        this.callbacks.onToggle(action.id);
        return;
      case 'delete':
        this.callbacks.onDelete(action.id);
        return;
      case 'exit':
        this.callbacks.onExit();
        return;
    }
  }

  dispose(): void {
    this.panel.geometry.dispose();
    this.panel.material.dispose();
    this.cursor.geometry.dispose();
    this.cursor.material.dispose();
    this.texture.dispose();
  }

  private setHover(key: string | undefined): void {
    if (this.hoverKey === key) return;
    this.hoverKey = key;
    this.paint();
  }

  private relayout(): void {
    this.layout = layoutMenu(this.models, this.scrollOffset);
    this.paint();
  }

  private paint(): void {
    const ctx = this.context;
    const { rows, regions, rangeLabel, totalCount } = this.layout;

    ctx.clearRect(0, 0, PANEL_WIDTH_PX, PANEL_HEIGHT_PX);

    roundRect(ctx, 0, 0, PANEL_WIDTH_PX, PANEL_HEIGHT_PX, 24);
    ctx.fillStyle = COLOURS.panel;
    ctx.fill();
    ctx.strokeStyle = COLOURS.panelEdge;
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';

    ctx.fillStyle = COLOURS.text;
    ctx.font = '600 40px system-ui, sans-serif';
    ctx.fillText('Models', 32, 72);

    // Sharing the title's baseline, right-aligned. Truncated well clear of "Models",
    // because an email address can be arbitrarily long and overlapping text on a panel
    // you cannot zoom is worse than an ellipsis.
    ctx.fillStyle = this.accountEmail === undefined ? COLOURS.textMuted : COLOURS.text;
    ctx.font = '400 24px system-ui, sans-serif';
    ctx.textAlign = 'right';
    fillTruncated(
      ctx,
      accountStatusLabel(this.accountEmail),
      PANEL_WIDTH_PX - 32,
      72,
      PANEL_WIDTH_PX - 32 - ACCOUNT_STATUS_LEFT_LIMIT,
    );
    ctx.textAlign = 'left';

    ctx.fillStyle = COLOURS.textMuted;
    ctx.font = '400 24px system-ui, sans-serif';
    ctx.fillText(
      roomStatusLabel(this.models.filter((model) => model.inRoom).length, totalCount),
      32,
      114,
    );

    if (rangeLabel !== undefined) {
      ctx.textAlign = 'right';
      ctx.fillText(rangeLabel, PANEL_WIDTH_PX - 32, 114);
      ctx.textAlign = 'left';
    }

    for (const row of rows) {
      const { toggleRect, deleteRect, model } = row;
      const rowHovered = this.hoverKey === `toggle:${model.id}`;
      const deleteHovered = this.hoverKey === `delete:${model.id}`;

      roundRect(
        ctx,
        toggleRect.x,
        toggleRect.y,
        toggleRect.width + deleteRect.width,
        toggleRect.height,
        12,
      );
      ctx.fillStyle = model.inRoom ? COLOURS.rowSelected : COLOURS.row;
      ctx.fill();

      if (rowHovered) {
        ctx.fillStyle = COLOURS.rowHover;
        ctx.fill();
      }

      if (model.inRoom) {
        ctx.strokeStyle = COLOURS.accent;
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // A filled disc for a model that is out in the room, a ring for one that is not.
      // Fill against outline reads at a glance and across the width of the panel, which
      // a difference in text or colour alone does not through a headset lens.
      const markerX = toggleRect.x + 20 + MARKER_RADIUS;
      const markerY = toggleRect.y + toggleRect.height / 2;
      ctx.beginPath();
      ctx.arc(markerX, markerY, MARKER_RADIUS, 0, Math.PI * 2);
      if (model.inRoom) {
        ctx.fillStyle = COLOURS.accent;
        ctx.fill();
      } else {
        ctx.strokeStyle = COLOURS.textMuted;
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      const textX = markerX + MARKER_RADIUS + 18;
      const rowEnd = toggleRect.x + toggleRect.width - 20;

      // The percentage sits at the end of the row rather than in with the dimensions,
      // where the truncation that keeps a long file name inside the panel would be free to
      // eat it. It is also the one part of the row that is a warning.
      const scaleWidth =
        model.scaleLabel === undefined
          ? 0
          : this.paintScale(
              model.scaleLabel,
              rowEnd,
              toggleRect.y + toggleRect.height / 2 + 9,
            );

      const textWidth = rowEnd - textX - scaleWidth;

      ctx.fillStyle = COLOURS.text;
      ctx.font = '500 28px system-ui, sans-serif';
      fillTruncated(ctx, model.name, textX, toggleRect.y + 34, textWidth);

      ctx.fillStyle = COLOURS.textMuted;
      ctx.font = '400 21px system-ui, sans-serif';
      fillTruncated(ctx, model.detail, textX, toggleRect.y + 60, textWidth);

      // Delete: a separator and a cross, rather than a full button, so it reads as part
      // of the row without being easy to hit by accident.
      ctx.beginPath();
      ctx.moveTo(deleteRect.x, deleteRect.y + 14);
      ctx.lineTo(deleteRect.x, deleteRect.y + deleteRect.height - 14);
      ctx.strokeStyle = COLOURS.panelEdge;
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.fillStyle = deleteHovered ? COLOURS.danger : COLOURS.textMuted;
      ctx.font = '400 34px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(
        '✕',
        deleteRect.x + deleteRect.width / 2,
        deleteRect.y + deleteRect.height / 2 + 12,
      );
      ctx.textAlign = 'left';
    }

    if (rows.length === 0) {
      ctx.fillStyle = COLOURS.textMuted;
      ctx.font = '400 26px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('No models in the library', PANEL_WIDTH_PX / 2, 340);
      ctx.textAlign = 'left';
    }

    for (const region of regions) {
      if (region.action.type === 'scroll') {
        this.paintButton(region, region.action.delta < 0 ? '▲' : '▼', 34);
      } else if (region.action.type === 'exit') {
        this.paintButton(region, 'Leave VR', 28);
      }
    }

    this.texture.needsUpdate = true;
  }

  /**
   * Draw a row's size percentage, right-aligned, and report the width it took.
   *
   * The width comes back so the name and the dimensions can be given the rest of the row
   * and no more. Measured rather than reserved, because the figures vary from "5%" to
   * "2000%" and a fixed allowance would either crowd the short ones or clip the long.
   */
  private paintScale(label: string, right: number, baseline: number): number {
    const ctx = this.context;

    ctx.font = '600 22px system-ui, sans-serif';
    ctx.fillStyle = COLOURS.resized;
    ctx.textAlign = 'right';
    ctx.fillText(label, right, baseline);
    ctx.textAlign = 'left';

    return ctx.measureText(label).width + SCALE_GAP;
  }

  private paintButton(region: MenuRegion, label: string, fontSize: number): void {
    const ctx = this.context;
    const { rect } = region;
    const hovered = this.hoverKey === region.key;

    roundRect(ctx, rect.x, rect.y, rect.width, rect.height, 12);
    ctx.fillStyle = region.enabled
      ? hovered
        ? COLOURS.buttonHover
        : COLOURS.row
      : COLOURS.buttonDisabled;
    ctx.fill();
    ctx.strokeStyle = hovered && region.enabled ? COLOURS.accent : COLOURS.panelEdge;
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.fillStyle = region.enabled ? COLOURS.text : COLOURS.textDisabled;
    ctx.font = `500 ${fontSize.toString()}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(label, rect.x + rect.width / 2, rect.y + rect.height / 2 + fontSize / 3);
    ctx.textAlign = 'left';
  }
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, radius);
}

/** Draw text, cutting it short with an ellipsis rather than letting it run over. */
function fillTruncated(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
): void {
  if (ctx.measureText(text).width <= maxWidth) {
    ctx.fillText(text, x, y);
    return;
  }

  let truncated = text;
  while (truncated.length > 1 && ctx.measureText(`${truncated}…`).width > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  ctx.fillText(`${truncated}…`, x, y);
}
